/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  var holidayCacheGlobal = {};
  var fetchedYearsGlobal = {};

  function parseBool(v) {
    return v === true || v === "true";
  }

  function postMiniCartForm(form) {
    if (!form) return;
    var checkoutBtn = form.querySelector('[name="checkout"]');
    if (checkoutBtn) checkoutBtn.disabled = true;
    var params = new URLSearchParams(new FormData(form));
    if (checkoutBtn) checkoutBtn.disabled = false;
    var rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    var url = rootPath.replace(/\/?$/, "/") + "cart";
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params.toString(),
    }).catch(function () {});
  }

  function initMiniCartDeliveryRoot(root) {
    if (!root || root.getAttribute("data-umatein-delivery-js") === "1") return;
    root.setAttribute("data-umatein-delivery-js", "1");

    var MIN_LEAD_DAYS = parseInt(root.dataset.minLeadDays, 10);
    var MAX_LEAD_DAYS = parseInt(root.dataset.maxLeadDays, 10);
    if (isNaN(MIN_LEAD_DAYS)) MIN_LEAD_DAYS = 2;
    if (isNaN(MAX_LEAD_DAYS)) MAX_LEAD_DAYS = 30;

    var DISABLE_WEEKDAYS = [];
    try {
      DISABLE_WEEKDAYS = JSON.parse(root.getAttribute("data-disable-weekdays") || "[]");
    } catch (e) {
      DISABLE_WEEKDAYS = [];
    }

    var DISABLE_HOLIDAYS = parseBool(root.dataset.disableHolidays);
    var USE_BUSINESS_DAYS_ONLY = parseBool(root.dataset.useBusinessDaysOnly);

    var dateInput = root.querySelector("[data-delivery-date-input]");
    var calGrid = root.querySelector(".cart-delivery__cal-grid");
    var calLabel = root.querySelector(".cart-delivery__cal-month-label");
    var calSelected = root.querySelector(".cart-delivery__cal-selected-text");
    var btnPrev = root.querySelector(".cart-delivery__cal-prev");
    var btnNext = root.querySelector(".cart-delivery__cal-next");

    if (!calGrid || !dateInput || !calLabel || !calSelected || !btnPrev || !btnNext) return;

    function pad(n) {
      return String(n).padStart(2, "0");
    }

    function formatDate(d) {
      return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
    }

    function formatDisplayDate(d) {
      var weeks = ["日", "月", "火", "水", "木", "金", "土"];
      return (
        d.getFullYear() + "年" + (d.getMonth() + 1) + "月" + d.getDate() + "日（" + weeks[d.getDay()] + "）"
      );
    }

    var today = new Date();
    today.setHours(0, 0, 0, 0);

    function isSameDay(a, b) {
      return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    function isHoliday(d) {
      if (!DISABLE_HOLIDAYS) return false;
      return !!holidayCacheGlobal[formatDate(d)];
    }

    function isDisabledDay(d) {
      if (d < minDate || d > maxDate) return true;
      if (DISABLE_WEEKDAYS.indexOf(d.getDay()) !== -1) return true;
      if (isHoliday(d)) return true;
      return false;
    }

    function addLeadDays(base, days) {
      if (days === 0) return new Date(base);
      var d = new Date(base);
      var added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (USE_BUSINESS_DAYS_ONLY) {
          var dow = d.getDay();
          if (dow !== 0 && dow !== 6 && !holidayCacheGlobal[formatDate(d)]) added++;
        } else {
          if (DISABLE_WEEKDAYS.indexOf(d.getDay()) === -1 && !isHoliday(d)) added++;
        }
      }
      return d;
    }

    var minDate, maxDate;
    var viewYear, viewMonth;
    var selectedDateStr = dateInput.value || "";
    var selectedDate = selectedDateStr ? new Date(selectedDateStr + "T00:00:00") : null;

    function fetchHolidaysForYear(year) {
      if (fetchedYearsGlobal[year]) return Promise.resolve();
      fetchedYearsGlobal[year] = true;
      var url = "https://holidays-jp.github.io/api/v1/" + year + "/date.json";
      return fetch(url)
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          Object.assign(holidayCacheGlobal, data);
        })
        .catch(function () {
          console.warn("[配送日時指定/mini-cart] 祝日データの取得に失敗（" + year + "年）");
        });
    }

    function renderCalendar() {
      var firstDay = new Date(viewYear, viewMonth, 1);
      var lastDay = new Date(viewYear, viewMonth + 1, 0);

      calLabel.textContent = viewYear + "年" + (viewMonth + 1) + "月";
      calGrid.innerHTML = "";

      for (var i = 0; i < firstDay.getDay(); i++) {
        var empty = document.createElement("span");
        empty.className = "cart-delivery__cal-day is-empty";
        calGrid.appendChild(empty);
      }

      for (var date = 1; date <= lastDay.getDate(); date++) {
        var d = new Date(viewYear, viewMonth, date);
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "cart-delivery__cal-day";
        btn.textContent = date;

        var dow = d.getDay();
        var holiday = DISABLE_HOLIDAYS ? holidayCacheGlobal[formatDate(d)] : null;

        if (dow === 0 || holiday) btn.classList.add("is-sun");
        if (dow === 6) btn.classList.add("is-sat");
        if (isSameDay(d, today)) btn.classList.add("is-today");
        if (selectedDate && isSameDay(d, selectedDate)) btn.classList.add("is-selected");

        if (holiday) btn.title = holiday;

        if (isDisabledDay(d)) {
          btn.disabled = true;
          btn.classList.add("is-disabled");
        } else {
          (function (day) {
            btn.addEventListener("click", function () {
              selectedDate = day;
              dateInput.value = formatDate(day);
              var form = dateInput.form || dateInput.closest("form");
              postMiniCartForm(form);
              calSelected.textContent = formatDisplayDate(day);
              calSelected.classList.add("has-value");
              renderCalendar();
            });
          })(d);
        }

        calGrid.appendChild(btn);
      }

      var prevMonthFirst = new Date(viewYear, viewMonth - 1, 1);
      var minMonthFirst = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      btnPrev.disabled = prevMonthFirst < minMonthFirst;

      var nextMonthFirst = new Date(viewYear, viewMonth + 1, 1);
      var maxMonthFirst = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
      btnNext.disabled = nextMonthFirst > maxMonthFirst;
    }

    function ensureHolidaysForView() {
      if (!DISABLE_HOLIDAYS) return Promise.resolve();
      return fetchHolidaysForYear(viewYear);
    }

    function initCal() {
      minDate = addLeadDays(today, MIN_LEAD_DAYS);
      maxDate = addLeadDays(today, MAX_LEAD_DAYS);
      viewYear = minDate.getFullYear();
      viewMonth = minDate.getMonth();

      if (selectedDate) {
        calSelected.textContent = formatDisplayDate(selectedDate);
        calSelected.classList.add("has-value");
        viewYear = selectedDate.getFullYear();
        viewMonth = selectedDate.getMonth();
      }

      btnPrev.addEventListener("click", function () {
        viewMonth--;
        if (viewMonth < 0) {
          viewMonth = 11;
          viewYear--;
        }
        ensureHolidaysForView().then(renderCalendar);
      });

      btnNext.addEventListener("click", function () {
        viewMonth++;
        if (viewMonth > 11) {
          viewMonth = 0;
          viewYear++;
        }
        ensureHolidaysForView().then(renderCalendar);
      });

      renderCalendar();
    }

    root.addEventListener("change", function (event) {
      var t = event.target;
      if (t && t.matches && t.matches("[data-delivery-time-input]")) {
        var form = dateInput.form || dateInput.closest("form");
        postMiniCartForm(form);
      }
    });

    if (DISABLE_HOLIDAYS || USE_BUSINESS_DAYS_ONLY) {
      var thisYear = today.getFullYear();
      Promise.all([fetchHolidaysForYear(thisYear), fetchHolidaysForYear(thisYear + 1)]).then(initCal);
    } else {
      initCal();
    }
  }

  function scanMiniCartDelivery() {
    var mini = document.getElementById("mini-cart");
    if (!mini) return;
    mini.querySelectorAll("[data-cart-delivery-root]").forEach(initMiniCartDeliveryRoot);
  }

  document.addEventListener("DOMContentLoaded", scanMiniCartDelivery);
  document.documentElement.addEventListener("cart:refresh", function () {
    var delays = [50, 200, 600, 900, 1200];
    requestAnimationFrame(function () {
      delays.forEach(function (ms) {
        setTimeout(scanMiniCartDelivery, ms);
      });
    });
  });
})();
